
import React, { Fragment, useCallback } from "react";
import {
    Box,
    Button,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useDropzone } from "react-dropzone";
import Close from "@mui/icons-material/Close";
import FileDocumentOutline from "mdi-material-ui/FileDocumentOutline";
import AddCircleIcon from '@mui/icons-material/AddCircle';

const DragDrop_Box_Style = {
    flexShrink: 0,
    //   background: "#F1F1F1",
    strokeWidth: "1px",
    //   stroke: "#007167",
};

function CustomFileUploads({
    multiple,
    files,
    onChange,
    minHeight = "100px",
    title,
    accept = ".jpg, .jpeg, .png, .pdf, .txt", // Default accept types, can be overridden
    MediaUrl,
}) {

    const AddShopImageText = styled(Typography)(({ theme }) => ({
        fontSize: "14px",
        fontWeight: 400,
        lineHeight: "19.6px",
        letterSpacing: "0.2px",
        color: "#000000",
    }));

    console.log("DEBUGfiles", files, "multiple:", multiple);

    const onDrop = useCallback(
        (acceptedFiles) => {
            if (multiple) {
                onChange([
                    ...(Array.isArray(files) ? files : []),
                    ...acceptedFiles,
                ]); // Pass files directly, no need to map
            } else {
                // For single file mode, replace the existing file
                onChange(acceptedFiles[0] || null);
            }
        },
        [files, multiple, onChange]
    );

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: accept, // Use accept prop for file type restrictions
        multiple: multiple, // Set to true if you want to allow multiple file selection
    });

    const handleRemoveFile = (file) => {
        if (multiple) {
            const uploadedFiles = Array.isArray(files) ? files : [];
            const filtered = uploadedFiles?.filter((i) => {
                // Handle string paths
                if (typeof file === "string" && typeof i === "string") {
                    return i !== file;
                }
                // Handle objects with filePath
                if (file?.filePath && i?.filePath) {
                    return i.filePath !== file.filePath;
                }
                // Handle File objects
                if (file instanceof File && i instanceof File) {
                    return i.name !== file.name;
                }
                // Handle mixed: file object vs File object
                if (file?.filePath && i instanceof File) {
                    return true; // Keep File objects
                }
                if (file instanceof File && i?.filePath) {
                    return true; // Keep file path objects
                }
                // Handle string vs object/file
                if (typeof file === "string" && (i?.filePath || i instanceof File)) {
                    return true; // Keep if different type
                }
                if ((file?.filePath || file instanceof File) && typeof i === "string") {
                    return true; // Keep if different type
                }
                return true; // Keep by default
            });
            onChange([...filtered]);
        } else {
            onChange(null);
        }
    };

    const renderFilePreview = (file) => {
        if (file?.type?.startsWith("image")) {
                return (
                    <img
                        width={38}
                        height={38}
                        alt={file.name}
                    src={URL.createObjectURL(file)} // Temporary URL for the image preview
                />
            );
        } else {
            return <FileDocumentOutline />;
        }
    };

    const renderFilePreviewFromURL = (fileURL) => {
        console.log("fileURL", fileURL, "MediaUrl", MediaUrl);

        if (!fileURL) {
            return <FileDocumentOutline />;
        }

        // Check if URL is already absolute (starts with http:// or https://)
        const isAbsoluteURL = fileURL.startsWith('http://') || fileURL.startsWith('https://');
        
        // Construct the full URL - if it's a relative path, prepend MediaUrl
        let fullURL = fileURL;
        if (!isAbsoluteURL && MediaUrl) {
            // Ensure MediaUrl doesn't end with / and fileURL doesn't start with /
            const baseUrl = MediaUrl.endsWith('/') ? MediaUrl.slice(0, -1) : MediaUrl;
            const filePath = fileURL.startsWith('/') ? fileURL : `/${fileURL}`;
            fullURL = `${baseUrl}${filePath}`;
        }

        console.log("fullURL", fullURL);

        const fileExtension = fileURL.split(".").pop()?.toLowerCase();
        const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp"];
        const audioExtensions = ["mp3", "wav", "ogg", "m4a"];
        
        if (imageExtensions.includes(fileExtension)) {
            return (
                <img
                    width={38}
                    height={38}
                    alt="File Preview"
                    src={fullURL}
                    style={{ objectFit: 'cover', borderRadius: '4px' }}
                />
            );
        } else if (audioExtensions.includes(fileExtension)) {
            // For audio files, show an audio icon or a simple audio player preview
            return (
                <Box
                    sx={{
                        width: 38,
                        height: 38,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '4px',
                    }}
                >
                    <Typography variant="caption" sx={{ fontSize: '10px', textAlign: 'center' }}>
                        🎵
                    </Typography>
                </Box>
            );
        } else {
            return <FileDocumentOutline />;
        }
    };

    const fileItem = (file) => {
        if (!file) return null;

        // Handle File objects first (newly uploaded files)
        if (file instanceof File) {
            return (
                <ListItem
                    key={file.name}
                    secondaryAction={
                        <IconButton edge="end" onClick={() => handleRemoveFile(file)}>
                            <Close fontSize="small" />
                        </IconButton>
                    }
                    sx={{p: 0}}
            >
                <ListItemAvatar>{renderFilePreview(file)}</ListItemAvatar>
                <ListItemText
                    primary={<Typography className="file-name">{file.name}</Typography>}
                    secondary={
                        <Typography className="file-size" variant="body2">
                            {Math.round(file?.size / 100) / 10 > 1000
                                ? `${(Math.round(file?.size / 100) / 10000).toFixed(1)} mb`
                                : `${(Math.round(file?.size / 100) / 10).toFixed(1)} kb`}
                        </Typography>
                    }
                />
            </ListItem>
        );
        }

        // Handle string paths (for icon and similar single file fields)
        if (typeof file === "string") {
            return (
                <ListItem
                    key={file}
                    sx={{p: 0}}
                    secondaryAction={
                        <IconButton edge="end" onClick={() => handleRemoveFile(file)}>
                            <Close fontSize="small" />
                        </IconButton>
                    }
                >
                    <ListItemAvatar>{renderFilePreviewFromURL(file)}</ListItemAvatar>
                    <ListItemText
                        primary={<Typography className="file-name">{file.split('/').pop()}</Typography>}
                    />
                </ListItem>
            );
        }

        // Handle objects with filePath and fileName (from API response)
        // Must check this after File instance check since File is also an object
        if (file && typeof file === "object" && file.filePath && !(file instanceof File)) {
            const fileURL = file.filePath;
            const fileName = file.fileName || file.filePath.split('/').pop();
            
            return (
                <ListItem
                    key={fileURL}
                    sx={{p: 0}}
                    secondaryAction={
                        <IconButton edge="end" onClick={() => handleRemoveFile(file)}>
                            <Close fontSize="small" />
                        </IconButton>
                    }
                >
                    <ListItemAvatar>{renderFilePreviewFromURL(fileURL)}</ListItemAvatar>
                    <ListItemText
                        primary={<Typography className="file-name">{fileName}</Typography>}
                    />
                </ListItem>
            );
        }

        return null;
    };

    // Normalize files: if multiple=false but files is an array, take the first element
    const normalizedFiles = multiple 
        ? files
        : (Array.isArray(files) && files.length > 0) 
            ? files[0] 
            : files;
    
    console.log("normalizedFiles", normalizedFiles, "from files:", files);

    const fileList = multiple
        ? Array.isArray(normalizedFiles) && normalizedFiles?.map((file) => fileItem(file))
        : normalizedFiles
            ? fileItem(normalizedFiles)
            : null;

    return (
        <div>
            <Box
                {...getRootProps()}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight={minHeight}
                border="2px dashed #00000070"
                borderRadius={"12px"}
                p={2}
                textAlign="center"
                sx={{ cursor: "pointer", ...DragDrop_Box_Style }}
                >
                    <input {...getInputProps()} />
                <div>
                    <Box>
                        <AddCircleIcon  sx={{ fontSize: '24px', color: '#00000070' }}/>
                    </Box>
                    <AddShopImageText>{title}</AddShopImageText>
                </div>
            </Box>

            {multiple && Array.isArray(normalizedFiles) && normalizedFiles?.length ? (
                <Fragment>
                    <List sx={{ p: 0 }}>{fileList}</List>
                </Fragment>
            ) : null}
            {!multiple && normalizedFiles ? (
                <Fragment>
                    <List sx={{ p: 0 }}>{fileList}</List>
                </Fragment>
            ) : null}
        </div>
    );
}

export default CustomFileUploads;
